import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import api from "../../../../services/api";
import Modal from "../../../../components/modal";
import SelectMonth from "../../../../components/selectMonth";

export default ({ values, projectId }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [epics, setEpics] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const fetchInvoices = async () => {
    try {
      const { ok, data } = await api.post(`/invoice/search`, { projectId, budgetIds: [values._id], per_page: 20000 });
      if (!ok) return;
      setInvoices(data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchEpics = async () => {
    if (values.facturation_type !== "forfait") return;
    try {
      const { ok, data } = await api.post(`/roadlog_item/search`, { budget_id: values._id, project_id: projectId });
      if (!ok) return;
      setEpics(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchEpics();
  }, [values._id]);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-300 rounded p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Invoices</h3>
          <button className="blue-btn" onClick={() => setInvoiceModalOpen(true)}>
            Generate invoice
          </button>
        </div>
        <div className="space-y-2">
          {invoices.map((invoice) => (
            <div key={invoice._id} className="flex items-center justify-between p-2 border rounded">
              <Link to={`/invoice/${invoice._id}`} target="_blank" className="text-sm underline text-blue-600">
                {invoice.name}
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{(invoice.netTotal || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span>
                {invoice.sent === "yes" ? (
                  <span className="border border-green-500 rounded text-green-500 px-1 text-xs">Sent</span>
                ) : (
                  <span className="border border-gray-500 rounded text-gray-500 px-1 text-xs">Not sent</span>
                )}
                {invoice.received === "yes" ? (
                  <span className="border border-green-500 rounded text-green-500 px-1 text-xs">Received</span>
                ) : invoice.sent === "yes" ? (
                  <span className="border border-red-500 rounded text-red-500 px-1 text-xs">Not received</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {values.facturation_type === "forfait" && (
        <div className="bg-white border border-gray-300 rounded p-4">
          <h3 className="text-lg font-semibold mb-4">Budget Epics</h3>
          {epics.length > 0 ? (
            <div className="space-y-2">
              {epics.map((epic) => (
                <div
                  key={epic._id}
                  className="p-3 bg-white rounded-lg border hover:shadow-sm hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/project/${projectId}/roadlog?item_modal_id=${epic._id}`)}>
                  <div className="flex justify-between items-center">
                    <div className="font-medium">{epic.title}</div>
                    <StatusTag status={epic.status} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
              <p className="text-gray-500">No epics associated with this budget</p>
            </div>
          )}
        </div>
      )}

      <GenerateInvoiceModal modalOpen={invoiceModalOpen} setModalOpen={setInvoiceModalOpen} budgetSelected={values} />
    </div>
  );
};

const StatusTag = ({ status }) => {
  if (!status) return null;
  const colors = {
    TODO: "bg-blue-600 text-xs text-blue-100 border-gray-300",
    READY_TO_DEV: "bg-pink-100 text-xs text-pink-800 border-pink-200",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800 border-blue-200",
    TESTING: "bg-purple-100 text-xs text-purple-800 border-purple-200",
    DONE: "bg-green-100 text-xs text-green-800 border-green-200",
    ARCHIVED: "bg-gray-200 text-xs text-gray-800 border-gray-300",
  };
  return <div className={`px-2 py-1 rounded w-fit border ${colors[status]}`}>{status}</div>;
};

const GenerateInvoiceModal = ({ modalOpen, setModalOpen, budgetSelected }) => {
  const [values, setValues] = useState({ name: budgetSelected?.name || "" });
  const navigate = useNavigate();

  const getInvoiceItems = async (budget) => {
    if (budget.facturation_type === "regie") {
      return [{
        name: budget.projectName || "Régie",
        price: budget.value || 0,
        quantity: 1,
        unit: "budget",
        totalPrice: budget.value || 0,
        description: "Prestation réalisée en régie sur la période convenue, facturation basée sur le temps passé et les ressources mobilisées.",
      }];
    }
    if (budget.facturation_type === "forfait") {
      const { ok, data } = await api.post(`/roadlog_item/search`, { budget_id: budget._id, statuses: ["TODO", "IN_PROGRESS", "DONE", "ARCHIVED"] });
      if (!ok) { toast.error("Failed to generate invoice"); return null; }
      return data.map((epic) => ({
        name: epic.title, description: epic.module || "", logo: epic.project_logo || "",
        price: epic.tjm, quantity: epic.days_required, unit: "days",
        totalPrice: Number((epic.days_required * epic.tjm || 0).toFixed(2)),
      }));
    }
    return [];
  };

  const generateInvoice = async () => {
    const itemsInvoice = await getInvoiceItems(budgetSelected);
    if (!itemsInvoice) return;
    const invoice = await api.post(`/invoice/`, {
      project_id: budgetSelected.projectId,
      budgetId: budgetSelected._id,
      name: values.name,
      date: values.date,
      itemsInvoice,
    });
    toast.success("Invoice generated successfully");
    navigate(`/invoice/${invoice.data._id}/editorbis`);
  };

  useEffect(() => {
    if (!budgetSelected) return;
    const currentDate = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1));
    setValues({ name: budgetSelected.name || "", date: currentDate });
  }, [budgetSelected]);

  return (
    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="w-2/3">
      <div className="px-16 py-12 space-y-8">
        <h2 className="text-xl font-semibold mb-4">Create New Invoice</h2>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="invoice-name">Invoice name</label>
          <input id="invoice-name" value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} placeholder="Invoice name" className="input" />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="invoice-date">Invoice Date</label>
          <SelectMonth key={budgetSelected?._id} id="invoice-date" placeholder="Select month" indexDefaultValue={0} start={0} value={values.date} onChange={(e) => setValues({ ...values, date: e.target.value })} />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="gray-btn" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="blue-btn" onClick={() => generateInvoice()}>Create</button>
        </div>
      </div>
    </Modal>
  );
};
